/**
 * @module lib/pdp-utils
 */

import Cookies from "js-cookie";
import isNil from "lodash/isNil";
import { ContentfulClient } from "@lululemon/ecom-pattern-library";
import { publicConfig } from "@lululemon/ecom-common/src/util/public-config";
import { get as lapelGet } from "@lululemon/mwa-ab-testing/esm/utils/storage";
import { trackEvent, EVENT_CATEGORY } from "@lululemon/mwa-analytics";
import { isValidProductId } from "@lululemon/ecom-common/src/util/product-utils";
import { makeGetGQLProductParams } from "@lululemon/ecom-common/src/util/graphql-utils";
import { scrollTo as animateScroll } from "../lib/animate-scroll";

// Query parameters accepted by ATG
export const ACCEPTABLE_URL_PARAMETERS = [
  "locale",
  "sl",
  "forceMemberCheck",
  "storeId",
];

export const PICKUP_IN_STORE = "PIS";

export const getSignpostingButtonText = (
  selectedSizeInStockParam,
  deliveryTypeParam
) => {
  if (!selectedSizeInStockParam) {
    return "select size";
  }

  if (selectedSizeInStockParam && deliveryTypeParam === PICKUP_IN_STORE) {
    return "buy & pick-up";
  }

  return "add to bag";
};

export const DELIVERY_TYPES = {
  BOPIS: "bopis",
  MIXED: "mixed",
};

export const getStyleId = (childSkus = [], swatchColorCode = "") => {
  let styleID = "";

  if (swatchColorCode === "" || !childSkus || childSkus.length === 0) {
    return styleID;
  }

  for (let i = 0; i < childSkus.length; i += 1) {
    if (childSkus[i]["color-code"] === swatchColorCode) {
      styleID = childSkus[i]["style-id"];
      break;
    }
  }
  return styleID;
};

export function boundsBy(array, iteratee = (item) => item) {
  const bounds = array.reduce(
    ([min, max], item) => {
      const value = parseFloat(iteratee(item));
      const newMin = Number.isFinite(value) ? Math.min(value, min) : min;
      const newMax = Number.isFinite(value) ? Math.max(value, max) : max;
      return [newMin, newMax];
    },
    [Infinity, -Infinity]
  );

  return bounds[0] === Infinity ? null : bounds;
}

export function getPriceOrRange(childSkus, colorDriver) {
  const isRange = (r) => Array.isArray(r) && r[0] !== r[1];
  // If min & max are the same, return a single value instead of a range
  const reduceRange = (r) => (r?.[0] === r?.[1] ? r?.[0] : r);
  const formatRange = (r) => (Array.isArray(r) ? `${r[0]} - ${r[1]}` : r);

  // The color driver only contains colors that are available
  const colorSet = new Set(colorDriver.map((c) => c.color));
  const availableColorSkus = childSkus.filter((sku) =>
    colorSet.has(sku.color.code)
  );
  const listPrice = reduceRange(
    boundsBy(availableColorSkus, (sku) => sku.price.listPrice)?.map((p) =>
      p.toFixed(2)
    )
  );
  const salePrice = reduceRange(
    boundsBy(availableColorSkus, (sku) => sku.price.salePrice)?.map((p) =>
      p.toFixed(2)
    )
  );
  return {
    currency: availableColorSkus[0]?.price?.currency ?? null,
    listPrice: formatRange(listPrice) ?? null,
    listPriceRange: isRange(listPrice) ? listPrice : null,
    salePrice: formatRange(salePrice) ?? null,
    salePriceRange: isRange(salePrice) ? salePrice : null,
    onSale: Boolean(salePrice),
    isRange: isRange(salePrice || listPrice),
  };
}

export function getFirstSkuByColorCode({ skus, colorCode }) {
  return skus?.find((sku) => sku.color?.code === colorCode);
}

export function getSkuByColorCodeAndSize({ skus, colorCode, size }) {
  return skus?.find(
    ({ color: skuColor, size: skuSize }) =>
      skuColor?.code === colorCode && skuSize === size
  );
}

export function getFirstSkuSinglePrice({ skus, activeColorCode }) {
  const { price } =
    getFirstSkuByColorCode({ skus, colorCode: activeColorCode }) || {};

  return {
    currency: price?.currency ?? null,
    listPrice: price?.listPrice ?? null,
    listPriceRange: null,
    salePrice: price?.salePrice ?? null,
    salePriceRange: null,
    onSale: Boolean(price?.salePrice),
    isRange: null,
  };
}

/**
 * @param options
 * @returns {string|{query: string, options: {}}}
 */
export const constructGraphQlQuery = (options) => {
  const [queryBody, queryOptions] = [[], {}];
  const { operation = "", fields = [], returns = "" } = options;
  if (!operation || fields.length <= 0) {
    throw new Error(
      "Requires operation and fields options to construct graphql query"
    );
  }
  fields.forEach(({ name, label, value, isRequired }) => {
    if (!value && isRequired) {
      throw new Error(
        `Required graphql field '${label || name}' cannot be empty.`
      );
    }
    if (label === "productId" || label === "id") {
      // we want to allow empty string for legacy resolvers
      if (isNil(value)) {
        throw new Error(
          `Required graphql field '${label || name}' cannot be empty.`
        );
      }
      if (!!value && !isValidProductId(value)) {
        throw new Error(
          `productId field must be in the format 'prod123456'. Received '${value}'`
        );
      }
    }
    queryBody.push(`${name}:$${label || name}`);
    if (typeof value !== "undefined") {
      queryOptions[name] = value;
    }
  });
  return {
    query: `${operation}(${queryBody.join(",")})${returns}`,
    options: queryOptions,
  };
};

export const getPdpGQLProductParams = makeGetGQLProductParams(
  ACCEPTABLE_URL_PARAMETERS
);

export const isClientSide = () => typeof window !== "undefined";

export const lapelGetABFlag = (ABFlagName) =>
  isClientSide() && !!lapelGet(ABFlagName);

export const emojiRegex =
  /[\u{1f300}-\u{1f5ff}\u{1f900}-\u{1f9ff}\u{1f600}-\u{1f64f}\u{1f680}-\u{1f6ff}\u{2600}-\u{26ff}\u{2700}-\u{27bf}\u{1f1e6}-\u{1f1ff}\u{1f191}-\u{1f251}\u{1f004}\u{1f0cf}\u{1f170}-\u{1f171}\u{1f17e}-\u{1f17f}\u{1f18e}\u{3030}\u{2b50}\u{2b55}\u{2934}-\u{2935}\u{2b05}-\u{2b07}\u{2b1b}-\u{2b1c}\u{3297}\u{3299}\u{303d}\u{00a9}\u{00ae}\u{2122}\u{23f3}\u{24c2}\u{23e9}-\u{23ef}\u{25b6}\u{23f8}-\u{23fa}]/gu;

export const createContentfulClient = (args = {}) => {
  const { accessToken, environmentId, previewToken, spaceId } =
    publicConfig?.contentful || {};

  return new ContentfulClient({
    accessToken,
    environmentId,
    previewToken,
    spaceId,
    locale: (Cookies.get("UsrLocale") || "").replace("_", "-"),
    isPreviewEnabled: false,
    ...args,
  });
};

/**
 * reference for holding the looked up dom element, so that a
 * dom lookup is not required every time a reference to this element is needed.
 */
let mobileNavRef;

/**
 * Method for clearing the reference to mobile nav element.
 * Useful for unit tests, to ensure unmounted references don't
 * prevent fetching the correct node.
 */
export const clearMobileNavRef = () => {
  mobileNavRef = null;
};

export const getMobileNavElement = () => {
  if (typeof window !== "undefined") {
    mobileNavRef = !mobileNavRef
      ? document.querySelector('[data-testid="nav-mobile"]')
      : mobileNavRef;
  }
  return mobileNavRef;
};

export const isMobileNavOpen = () =>
  !!getMobileNavElement() &&
  getComputedStyle(getMobileNavElement())?.top === "0px";

export const isElementAtTopOfMobileViewPort = (elem) => {
  const mobileNav = getMobileNavElement();
  const navHeight = mobileNav
    ? parseInt(getComputedStyle(mobileNav).height, 10)
    : 0;
  const { top } = elem.getBoundingClientRect();
  return isMobileNavOpen()
    ? top > navHeight - 1 && top < navHeight + 1
    : top >= 0 && top <= 20;
};

// TODO: improve the implementation of isElementHidden()
/**
 * Utility method to check if a html element is visible on the screen
 * NB: this does not factor in elements hidden by offset/scrolling,
 * just those hidden by opacity, visibility, display css props and zero height/width.
 * It tests visibility of the element, then that of each enclosing parent element
 * until it gets to the element with selector matching `parentSelector`
 * This is not a very efficient algorithm, so use sparingly. Thanks!
 * @param {HTMLElement} elem - element whose visibility is being tested
 * @param {string} parentSelector - selector of element within which the traversal happens
 * @returns {boolean}
 */
export const isElementHidden = (elem, parentSelector = "#app") => {
  if (!elem) return true;
  const outermostParent = document.querySelector(parentSelector);
  const visibilityAttributes = {
    height: "0",
    width: "0",
    opacity: "0",
    display: "none",
    visibility: "hidden",
  };
  function isVisible(node) {
    let visible = true;
    Object.keys(visibilityAttributes).every((key) => {
      const style = getComputedStyle(node);
      if (
        style?.[key] === visibilityAttributes[key] ||
        `${parseInt(style?.[key], 10)}` === visibilityAttributes[key]
      ) {
        visible = false;
        return false; // break out of loop if node is hidden
      }
      return true; // continue loop if node is visible
    });
    return visible;
  }
  let isHidden = !isVisible(elem);
  if (isHidden) return true;
  if (!outermostParent) return isHidden;
  let parent = elem;
  while (parent && parent !== outermostParent) {
    parent = parent?.parentElement;
    if (!isVisible(parent)) {
      isHidden = true;
      break;
    }
    // ultimately exit the loop if traversal gets to body
    if (parent?.nodeName === "BODY") break;
  }
  return isHidden;
};

const toParsedSize = (strSize) => parseInt(strSize.split(" ")[1], 10);
const toSortedModels = (modelA, modelB) =>
  toParsedSize(modelA.modelSize) - toParsedSize(modelB.modelSize);

export const splitModelData = (modelDescription) => {
  if (typeof modelDescription !== "string") return null;

  const personsHeightRegex = /\d('|’|`)((\s*)(\d){1,2}(''|"|”)){0,1}/g;
  const sizeRegex = /(Size|size)\s{1}\d+/g;

  const [name] = modelDescription.split(" ");

  return {
    name,
    modelHeight: modelDescription?.match(personsHeightRegex)?.[0] || "",
    modelSize: modelDescription?.match(sizeRegex)?.[0] || "",
  };
};

const toSplitModelObjArray = (allModels, carouselItem) => {
  const colorCode = carouselItem.color?.code;
  const modelDescription = carouselItem.modelInfo?.description;
  const modelFullBodyImage = carouselItem.imageInfo?.[1];

  if (!modelDescription || !modelFullBodyImage || !colorCode) return allModels;

  const modelSplitData = {
    colorCode,
    modelImage: modelFullBodyImage,
    ...splitModelData(modelDescription),
  };

  // Checking for all data to be available
  if (
    !modelSplitData?.name ||
    !modelSplitData?.modelHeight ||
    !modelSplitData?.modelSize
  ) {
    return allModels;
  }

  // Sizes must not be repeated
  // TODO: save the newest (last added) model when multiple models have the same size...
  if (
    !allModels.find(({ modelSize }) => modelSize === modelSplitData.modelSize)
  ) {
    allModels.push(modelSplitData);
  }

  return allModels;
};

/**
 * Select 4 models including the ones with smallest size and largest size.
 * Remaining models should be randomized.
 * The final array order should be randomized.
 */
const selectFourModels = (modelsArray) => {
  const sortedModel = modelsArray.sort(toSortedModels);
  const smallestSizeModel = sortedModel.shift();
  const largestSizeModel = sortedModel.pop();
  const [thirdModel] = sortedModel.splice(
    Math.floor(Math.random() * (sortedModel.length - 1)),
    1
  );
  const [fourthModel] = sortedModel.splice(
    Math.floor(Math.random() * (sortedModel.length - 1)),
    1
  );

  return [smallestSizeModel, thirdModel, fourthModel, largestSizeModel].sort(
    () => 0.5 - Math.random()
  );
};

export const getChangeModelModalData = (productCarouselData) => {
  if (!Array.isArray(productCarouselData) || !productCarouselData?.length)
    return [];
  const modelsArray = productCarouselData.reduce(toSplitModelObjArray, []);
  return modelsArray.length <= 4 ? modelsArray : selectFourModels(modelsArray);
};

export const trackChangeModelInteraction = ({
  id,
  type,
  text = "",
  position = -1,
  totalCount = -1,
  attributes,
}) =>
  trackEvent(EVENT_CATEGORY.GENERAL_COMPONENT_INTERACTION, {
    component: {
      id,
      type,
      text,
      position,
      totalCount,
      attributes,
    },
  });

export const getDefaultColorwaysData = (expandedColorwaysData) => {
  if (
    !expandedColorwaysData ||
    typeof expandedColorwaysData !== "object" ||
    Array.isArray(expandedColorwaysData) ||
    !Object.keys(expandedColorwaysData).length
  )
    return [];
  const [[colorCode, firstAvailableColor]] = Object.entries(
    expandedColorwaysData
  );
  // Returns black color as default if available otherwise will return first available color
  return expandedColorwaysData["0001"]
    ? [expandedColorwaysData["0001"], "0001"]
    : [firstAvailableColor, colorCode];
};

export const formatColorwaysData = (consistentColorSet, colorCode) => {
  if (!Array.isArray(consistentColorSet) || !consistentColorSet?.length)
    return [];

  return consistentColorSet
    .map(({ modelInfo, imagery }) => {
      const { name, modelHeight, modelSize } = modelInfo;
      const [, modelImage] = imagery;
      return {
        colorCode,
        modelImage,
        name,
        modelHeight,
        modelSize,
        fullImagery: imagery,
      };
    })
    .sort(() => 0.5 - Math.random());
};

export function scrollElementIntoView(element, navHeight, duration) {
  if (element.current && typeof window !== "undefined") {
    const { top: elementTop, bottom: elementBottom } =
      element.current.getBoundingClientRect();
    const viewportTop = navHeight;
    const viewportBottom = window.scrollY + window.innerHeight;
    const isElementInView =
      elementTop >= viewportTop && elementBottom <= viewportBottom;
    const scrollElement = document.scrollingElement || document.documentElement;
    const elDistanceToTop = window.scrollY + Math.floor(elementTop) - navHeight;

    if (!isElementInView) {
      animateScroll(scrollElement, elDistanceToTop, duration);
    }
  }
}

/**
 * Determines if the current page was directly accessed based on navigation history.
 *
 * Evaluates direct access by checking 'lll-nav-history' in sessionStorage. To qualify,
 * 'lll-nav-history' must be a single-entry array matching the current pathname.
 * Returns false if executed outside a browser context, if 'lll-nav-history' is missing,
 * not an array, has more than one entry, or its entry doesn't match the current pathname.
 *
 * @returns {boolean} True for direct access under the specified conditions, otherwise false.
 */

export function isDirectPageEntry() {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    const navHistoryItem = sessionStorage.getItem("lll-nav-history");

    if (!navHistoryItem) {
      return false;
    }
    const lllNavHistory = JSON.parse(navHistoryItem);
    if (!Array.isArray(lllNavHistory) || lllNavHistory.length !== 1) {
      return false;
    }
    return lllNavHistory[0] === window.location.pathname;
  } catch (error) {
    console.log("Error while parsing lll-nav-history", error);
    return false;
  }
}
